/**
 * The duplicates screen's two calls: what looks like one company, and the join
 * that makes it one.
 *
 * Its own file rather than another block in `clients.ts`, because the question
 * belongs to neither table: every answer here is about a lead AND a client at
 * once, and the shapes (a pair, a rule, what a merge moved) are shared by
 * nothing else.
 */
import { api } from "./api";

export type PartyKind = "lead" | "client";
export type DuplicateRule = "reg_code" | "name" | "email";
export type DuplicateConfidence = "certain" | "probable" | "weak";

export interface PartyRef {
  kind: PartyKind;
  id: string;
  name: string;
}

export interface DuplicatePair {
  key: string;
  /** The older record. The dialog offers this one as the survivor. */
  a: PartyRef;
  b: PartyRef;
  rules: DuplicateRule[];
  confidence: DuplicateConfidence;
}

/** A value shared by too many rows to be a duplicate - one accountant's address
 *  on nine companies. Reported rather than silently dropped. */
export interface DuplicateSkip {
  rule: DuplicateRule;
  value: string;
  size: number;
}

export interface DuplicateScan {
  pairs: DuplicatePair[];
  skipped: DuplicateSkip[];
  scanned: { leads: number; clients: number };
  truncated: boolean;
}

export interface MergeMove {
  model: string;
  field: string;
  moved: number;
  /** Rows that could not follow: the keeper already held that slot. */
  kept: number;
}

export interface MergeOutcome {
  keep: PartyRef;
  merged: PartyRef;
  moves: MergeMove[];
  filled: string[];
  tagsAdded: string[];
  closedAs: "lost" | "inactive";
}

export const getDuplicates = () => api.get<DuplicateScan>("/admin/crm/duplicates");

export const mergeDuplicate = (body: {
  keep: { kind: PartyKind; id: string };
  merge: { kind: PartyKind; id: string };
}) => api.post<MergeOutcome>("/admin/crm/duplicates/merge", body);
